/**
 * OnboardingAgentStep: the wizard's optional last step, after the tag and the
 * connectors (Paper "08 - API vs Platform · v2" → 05/06 Onboarding · Agent).
 *
 * The person adds the hosted MCP server to their agent (connector URL, CLI
 * command or Cursor install link). The agent then runs MCP sign-in, which
 * opens `/mcp-connect` to allow access, so no key is pasted by hand. Once
 * connected, the agent sets the site up itself through the MCP tools.
 *
 * "First question asked" is read from the account keys' `last_used_at`: a key
 * used after this step opened means an agent called. That is the only signal
 * today; an agent activity log is a backend ask (docs/TEMP_API_FIRST_CONTRACT.md).
 * A 403 on the key list (plan without API access) stops the check quietly and
 * leaves the step skippable.
 */

import { useEffect, useRef, useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { Copy01Icon, Tick01Icon } from "@hugeicons/core-free-icons"
import { toast } from "sonner"

import { getAcquisitionContext, getSignupPath } from "@/lib/utils/acquisitionContext"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { ApiAccessForbiddenError, getAccountApiKeys } from "@/lib/api/api-keys"
import { MCP_URL } from "@/lib/constants/agentConnect"
import claudeMark from "@/assets/logos/agents/claude.svg"
import codexMark from "@/assets/logos/agents/codex.svg"
import cursorMark from "@/assets/logos/agents/cursor.svg"
import openaiMark from "@/assets/logos/llm/openai.svg"

type AgentClientId = "claude" | "claude-code" | "chatgpt" | "codex" | "cursor" | "other"

const CLAUDE_CODE_COMMAND = `claude mcp add --transport http humblytics ${MCP_URL}`

/** Cursor's one-click MCP install link: base64 of the server's mcp.json entry. */
const CURSOR_INSTALL_URL = `cursor://anysphere.cursor-deeplink/mcp/install?name=humblytics&config=${btoa(
  JSON.stringify({ url: MCP_URL }),
)}`

const FIRST_MESSAGE = "Set up Humblytics on my website."

/** The agent tab and site picked on the marketing site's setup card, if this sign-up came from it. */
function marketingPick(): { clientId: AgentClientId; firstMessage: string } {
  const context = getSignupPath() === "agent" ? getAcquisitionContext() : {}
  const known: AgentClientId[] = ["claude", "claude-code", "chatgpt", "codex", "cursor"]
  const agent = context.agent as AgentClientId | undefined
  const site = (context.site || "").replace(/[^a-z0-9.-]/gi, "")
  return {
    clientId: agent && known.includes(agent) ? agent : agent ? "other" : "claude",
    firstMessage: site ? `Set up Humblytics on ${site}.` : FIRST_MESSAGE,
  }
}

type AgentClient = {
  id: AgentClientId
  name: string
  mark?: string
  /** What to copy into the client: the server URL, or a command. */
  copy: string
  where: string
}

const CLIENTS: AgentClient[] = [
  { id: "claude", name: "Claude", mark: claudeMark, copy: MCP_URL, where: "Settings → Connectors → Add custom connector." },
  { id: "claude-code", name: "Claude Code", mark: claudeMark, copy: CLAUDE_CODE_COMMAND, where: "Run it in your terminal." },
  { id: "chatgpt", name: "ChatGPT", mark: openaiMark, copy: MCP_URL, where: "Settings → Connectors, with developer mode on. Add a connector with this URL." },
  { id: "cursor", name: "Cursor", mark: cursorMark, copy: MCP_URL, where: "Or Settings → MCP → Add new MCP server with this URL." },
  { id: "codex", name: "Codex", mark: codexMark, copy: MCP_URL, where: "Add it as a remote MCP server." },
  { id: "other", name: "Other", copy: MCP_URL, where: "Add it as a remote MCP server (Streamable HTTP)." },
]

/** Plain names first, the tool name under it. Real MCP tool names only. */
const CAPABILITIES: { label: string; tool: string; needsLaunchAccess?: boolean }[] = [
  { label: "Read traffic and sources", tool: "get_traffic_summary" },
  { label: "Find where people leave", tool: "query_funnel" },
  { label: "See which ads make money", tool: "get_ads_attribution" },
  { label: "Suggest tests to run", tool: "get_split_test_recommendations" },
  { label: "Launch a test", tool: "create_split_test", needsLaunchAccess: true },
]

const STARTER_QUESTIONS = [
  "What's my top traffic source this week?",
  "Where do visitors leave my pricing page?",
]

const POLL_MS = 5_000

type Detection = "listening" | "connected" | "unavailable"

/** Polls the account keys until one is used after `since`. */
function useFirstAgentCall(token: string | null, since: number): { state: Detection; at: string | null } {
  const [state, setState] = useState<Detection>("listening")
  const [at, setAt] = useState<string | null>(null)

  useEffect(() => {
    if (!token || state !== "listening") return
    let cancelled = false
    const check = async () => {
      try {
        const keys = await getAccountApiKeys(token)
        const used = keys
          .filter((k) => k.isActive && k.lastUsedAt && Date.parse(k.lastUsedAt) >= since)
          .sort((a, b) => Date.parse(b.lastUsedAt!) - Date.parse(a.lastUsedAt!))[0]
        if (!cancelled && used) {
          setAt(used.lastUsedAt)
          setState("connected")
        }
      } catch (error) {
        if (!cancelled && error instanceof ApiAccessForbiddenError) setState("unavailable")
        // Anything else: keep listening, the next tick retries.
      }
    }
    void check()
    const id = window.setInterval(check, POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [token, since, state])

  return { state, at }
}

type OnboardingAgentStepProps = {
  token: string | null
  /** The primary exit's label. Names the trial when the account has no billing yet. */
  dashboardLabel: string
  /** Primary exit once an agent has called, or the skip link. */
  onFinish: (outcome: { connected: boolean; client: AgentClientId }) => void
  onClientChange?: (client: AgentClientId) => void
  onPromptCopied?: (client: AgentClientId) => void
  /**
   * Agent path (step 02 = "With your agent"): the agent is connected before the
   * script, so the next step is the agent adding it. Changes the copy, the
   * skip link and the first capability; nothing else.
   */
  installNext?: boolean
  className?: string
}

export function OnboardingAgentStep({ token, dashboardLabel, onFinish, onClientChange, onPromptCopied, installNext = false, className }: OnboardingAgentStepProps) {
  const [pick] = useState(marketingPick)
  const [clientId, setClientId] = useState<AgentClientId>(pick.clientId)
  const [copied, setCopied] = useState(false)
  const openedAtRef = useRef(Date.now())
  const { state, at } = useFirstAgentCall(token, openedAtRef.current)

  const client = CLIENTS.find((c) => c.id === clientId) ?? CLIENTS[0]
  const connected = state === "connected"
  const [copiedMessage, setCopiedMessage] = useState(false)

  const copyText = async (text: string, done: (value: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text)
      done(true)
      window.setTimeout(() => done(false), 1500)
      return true
    } catch {
      toast.error("Couldn't copy. Select the text and copy it instead.")
      return false
    }
  }

  const copySetup = async () => {
    if (await copyText(client.copy, setCopied)) onPromptCopied?.(clientId)
  }

  return (
    <section aria-labelledby="onboarding-agent-heading" className={cn("grid gap-8 lg:grid-cols-[minmax(0,1fr)_288px]", className)}>
      <div className="flex min-w-0 flex-col gap-5">
        <div className="flex flex-col gap-2">
          <h1
            id="onboarding-agent-heading"
            className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground sm:text-[28px] sm:leading-[34px]"
          >
            {connected ? "Your agent is connected" : "Connect your agent"}
          </h1>
          <p className="text-sm leading-5 text-muted-foreground sm:text-base sm:leading-6">
            {installNext
              ? connected
                ? "Ask it to set up Humblytics. It adds your site and the tracking script."
                : "Then it adds your site and the tracking script through Webflow, Framer or GitHub."
              : connected
                ? "Tests it launches show up in Agents. You can stop any of them there."
                : "Your agent can read your numbers and suggest tests. It can't launch one unless you turn that on for its key."}
          </p>
        </div>

        <div className="well flex flex-col gap-2 rounded-xl bg-muted p-2">
          <div role="tablist" aria-label="Your agent" className="flex gap-1 overflow-x-auto rounded-lg bg-card p-1 shadow-[0_0_0_1px_var(--border)]">
            {CLIENTS.map((c) => {
              const active = c.id === clientId
              return (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setClientId(c.id)
                    setCopied(false)
                    onClientChange?.(c.id)
                  }}
                  className={cn(
                    "flex min-h-9 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-2 text-[13px] transition-colors",
                    active ? "bg-muted font-medium text-foreground shadow-[0_0_0_1px_var(--input)]" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {c.mark ? <img src={c.mark} alt="" className="size-3.5 dark:invert" /> : null}
                  {c.name}
                </button>
              )
            })}
          </div>

          <ol className="flex flex-col rounded-lg bg-card shadow-[0_0_0_1px_var(--border),0_1px_2px_rgb(0_0_0/0.04)]">
            <Row n={1} done={connected} title={client.copy === MCP_URL ? `Add Humblytics to ${client.name}` : "Run this command"}>
              {client.id === "cursor" ? (
                <Button asChild variant="outline" size="sm" className="h-9 w-fit gap-1.5 rounded-lg bg-card px-3 text-xs">
                  <a href={CURSOR_INSTALL_URL} onClick={() => onPromptCopied?.(clientId)}>
                    <img src={cursorMark} alt="" className="size-3.5 dark:invert" />
                    Add to Cursor
                  </a>
                </Button>
              ) : null}
              <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-2 pl-3 shadow-[0_0_0_1px_var(--border)]">
                <code className="min-w-0 flex-1 truncate font-mono text-xs leading-[18px] text-foreground/80">{client.copy}</code>
                <Button type="button" variant="outline" size="sm" onClick={copySetup} className="h-8 shrink-0 gap-1.5 rounded-lg bg-card px-3 text-xs">
                  <HugeiconsIcon icon={copied ? Tick01Icon : Copy01Icon} size={14} />
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <p className="text-xs leading-[17px] text-muted-foreground">{client.where}</p>
            </Row>
            <Row n={2} done={connected} title="Allow access">
              <p className="text-xs leading-[17px] text-muted-foreground">
                A Humblytics page opens. Sign in and click Allow access. No key to copy.
              </p>
            </Row>
            <Row n={3} done={connected} title={installNext ? "Ask it to set up Humblytics" : "Ask your first question"} last>
              {installNext ? (
                <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-2 pl-3 shadow-[0_0_0_1px_var(--border)]">
                  <span className="min-w-0 flex-1 text-xs leading-[18px] text-foreground">{pick.firstMessage}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void copyText(pick.firstMessage, setCopiedMessage)}
                    className="h-8 shrink-0 gap-1.5 rounded-lg bg-card px-3 text-xs"
                  >
                    <HugeiconsIcon icon={copiedMessage ? Tick01Icon : Copy01Icon} size={14} />
                    {copiedMessage ? "Copied" : "Copy"}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {STARTER_QUESTIONS.map((q) => (
                    <span key={q} className="rounded-lg px-2.5 py-1.5 text-xs text-foreground shadow-[0_0_0_1px_var(--input)]">
                      {q}
                    </span>
                  ))}
                </div>
              )}
            </Row>
          </ol>

          <StatusRow state={state} at={at} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => onFinish({ connected, client: clientId })}
            className="min-h-6 text-sm leading-[18px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            {installNext
              ? "Set it up in the app instead"
              : connected
                ? "Connect another agent later"
                : "Skip for now and open my dashboard"}
          </button>
          <Button
            type="button"
            disabled={!connected}
            onClick={() => onFinish({ connected: true, client: clientId })}
            className="h-11 rounded-lg bg-[var(--onboarding-action,oklch(0.54_0.21_42))] px-5 text-sm font-medium text-white hover:bg-[var(--onboarding-action,oklch(0.54_0.21_42))] hover:opacity-90"
          >
            {dashboardLabel}
          </Button>
        </div>
      </div>

      <aside aria-label="What your agent can do" className="flex flex-col gap-3 lg:pt-[86px]">
        <p className="text-xs text-muted-foreground">What your agent can do</p>
        <ul className="flex flex-col gap-3 rounded-xl bg-muted p-4">
          {installNext ? (
            <li className="flex flex-col gap-0.5">
              <span className="text-[13px] text-foreground">Add your site and tracking script</span>
              <span className="text-[11px] text-muted-foreground">create_property, get_install_status</span>
            </li>
          ) : null}
          {CAPABILITIES.map((c) => (
            <li key={c.tool} className={cn("flex flex-col gap-0.5", c.needsLaunchAccess && "border-t border-border pt-3")}>
              <span className="flex items-center gap-1.5 text-[13px] text-foreground">
                {c.label}
                {c.needsLaunchAccess ? (
                  <span className="rounded-[7px] bg-muted-foreground/10 px-1.5 text-[11px] font-medium text-muted-foreground">Off by default</span>
                ) : null}
              </span>
              <span className="text-[11px] text-muted-foreground">{c.tool}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs leading-[18px] text-muted-foreground">
          Questions your agent asks are not counted. You only pay for data points your site sends.
        </p>
      </aside>
    </section>
  )
}

function Row({ n, done, title, last, children }: { n: number; done: boolean; title: string; last?: boolean; children: React.ReactNode }) {
  return (
    <li className={cn("flex gap-3.5 px-4 py-3.5 sm:px-5", !last && "border-b border-ui-hairline")}>
      <span
        aria-hidden
        className={cn(
          "flex size-[22px] shrink-0 items-center justify-center rounded-full text-[11px] tabular-nums",
          done ? "bg-success-tint text-success" : "border border-border text-foreground/75",
        )}
      >
        {done ? <HugeiconsIcon icon={Tick01Icon} size={12} /> : n}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="text-sm font-medium leading-[22px] text-foreground">{title}</p>
        {done ? null : children}
      </div>
    </li>
  )
}

function StatusRow({ state, at }: { state: Detection; at: string | null }) {
  if (state === "connected") {
    const time = at ? new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null
    return (
      <div role="status" className="flex items-center gap-2.5 rounded-lg bg-success-tint px-4 py-3 shadow-[0_0_0_1px_var(--border)]">
        <span aria-hidden className="size-2 shrink-0 rounded-full bg-live" />
        <p className="flex-1 text-[13px] font-medium text-foreground">Your agent asked its first question. You're all set.</p>
        {time ? <span className="text-[11px] text-muted-foreground">{time}</span> : null}
      </div>
    )
  }
  if (state === "unavailable") {
    return (
      <div role="status" className="rounded-lg bg-card px-4 py-3 text-[13px] text-muted-foreground shadow-[0_0_0_1px_var(--border)]">
        Agent access isn't on this plan. Connect it later from API &amp; MCP.
      </div>
    )
  }
  return (
    <div role="status" className="flex items-center gap-2.5 rounded-lg bg-card px-4 py-3 shadow-[0_0_0_1px_var(--border)]">
      <span aria-hidden className="size-2 shrink-0 animate-pulse rounded-full bg-muted-foreground/60 motion-reduce:animate-none" />
      <p className="flex-1 text-[13px] text-foreground">Waiting for your agent's first question.</p>
      <span className="text-[11px] text-muted-foreground">Listening</span>
    </div>
  )
}
